import React from 'react';
import { Link } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { 
  Heart, 
  ShoppingBag, 
  Trash2, 
  Sparkles, 
  ArrowLeft,
  ShieldCheck,
  ArrowRight
} from 'lucide-react';
import { formatINR } from '../data/products';

export default function WishlistPage() {
  const {
    products,
    wishlist,
    toggleWishlist,
    addToCart,
    masterMetalRates,
    calculateProductPrice
  } = useShop();

  const safeWishlist = Array.isArray(wishlist) ? wishlist : [];
  const safeProducts = Array.isArray(products) ? products : [];
  const wishlistedProducts = safeProducts.filter((p) => p && safeWishlist.includes(p.id));

  const handleMoveToBag = (product) => {
    if (!product) return;
    addToCart(product, 1);
    toggleWishlist(product.id);
  };

  return (
    <div style={{ minHeight: '80vh', backgroundColor: '#07070b', color: '#fff', padding: '2rem 1rem 5rem' }}>
      <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
        {/* Breadcrumb Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.75rem' }}>
          <Link to="/" style={{ color: 'var(--gold-400)', textDecoration: 'none' }}>Home</Link>
          <span>/</span>
          <span style={{ color: '#fff' }}>Royal Wishlist ({safeWishlist.length})</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.85rem', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Heart color="#f43f5e" fill="#f43f5e" size={28} />
              Your Royal Wishlist
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Keep track of dream jewellery pieces, compare styles, and order with 1-click VIP delivery.
            </p>
          </div>
          <Link 
            to="/" 
            className="btn btn-secondary btn-sm"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
          >
            <ArrowLeft size={16} /> Continue Exploring
          </Link>
        </div>

        {wishlistedProducts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '5rem 1.5rem',
            backgroundColor: '#0c0c14',
            borderRadius: '16px',
            border: '1px solid var(--border-gold)'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'rgba(244, 63, 94, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              border: '1px solid rgba(244, 63, 94, 0.25)'
            }}>
              <Heart size={40} color="#f43f5e" />
            </div>
            <h2 style={{ color: '#fff', fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.5rem' }}>
              Your Wishlist is Empty
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', maxWidth: '480px', margin: '0 auto 2rem' }}>
              Explore our handcrafted gold necklaces, antique polki chokers, solitaires, and luxury kitchenware to save your favorites.
            </p>
            <Link 
              to="/" 
              className="btn btn-gold"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 2rem' }}
            >
              <Sparkles size={18} /> Discover Jewellery
            </Link>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1.75rem'
          }}>
            {wishlistedProducts.map((product) => {
              const pricing = calculateProductPrice ? calculateProductPrice(product, masterMetalRates) : null;
              const displayPrice = pricing?.finalPrice || Number(product.price) || 0;

              return (
                <div 
                  key={product.id}
                  style={{
                    backgroundColor: '#0c0c14',
                    borderRadius: '16px',
                    border: '1px solid var(--border-gold)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                >
                  {/* Image container */}
                  <Link 
                    to={`/product/${product.id}`}
                    style={{ position: 'relative', width: '100%', height: '260px', overflow: 'hidden', display: 'block', backgroundColor: '#12121e' }}
                  >
                    <img 
                      src={product.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=600&q=80'} 
                      alt={product.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      backgroundColor: 'rgba(7, 7, 11, 0.85)',
                      backdropFilter: 'blur(8px)',
                      color: 'var(--gold-400)',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: '999px',
                      border: '1px solid var(--border-gold)'
                    }}>
                      {product.metal || '22K Gold'}
                    </div>
                  </Link>

                  {/* Body */}
                  <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Link 
                      to={`/product/${product.id}`}
                      style={{
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '1rem',
                        textDecoration: 'none',
                        marginBottom: '0.4rem',
                        lineHeight: 1.4
                      }}
                    >
                      {product.name}
                    </Link>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                      <span>Weight: {product.weight || '8.5g'}</span>
                      <span>•</span>
                      <span style={{ color: '#34d399' }}>BIS 916 Hallmarked</span>
                    </div>

                    <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--gold-400)', marginBottom: '1rem' }}>
                        {formatINR(displayPrice)}
                      </div>

                      <div style={{ display: 'flex', gap: '0.6rem' }}>
                        <button
                          onClick={() => handleMoveToBag(product)}
                          className="btn btn-gold btn-sm"
                          style={{
                            flex: 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.4rem'
                          }}
                        >
                          <ShoppingBag size={15} /> Move to Bag
                        </button>
                        <button
                          onClick={() => toggleWishlist(product.id)}
                          className="btn btn-secondary btn-sm"
                          style={{
                            color: '#f87171',
                            padding: '0 0.75rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Remove from Wishlist"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
